import type { Locale } from '@seek/melways-sites';
import { Box, Divider, Text, TextLinkButton } from 'braid-design-system';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import { downloadFile } from 'src/libs/fileDownload';
import { attachmentUrlV2 } from 'src/utils/attachmentUrls';
import { trackDownloadDocumentEvent } from 'src/utils/events';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';

import Item from '../Item/Item';
import type { Attachment } from '../NotesAttachmentList/NotesAttachmentList';

interface Props {
  attachmentItem: Attachment;
  locale: Locale;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
  jobId: string;
  applicationCorrelationId: string;
  onDelete: (attachmentItem: Attachment) => Promise<'done' | 'error'>;
  jobApplicationId: string;
  seekCandidateId: string;
  candidateId: string;
}

export const AttachmentItem = ({
  attachmentItem,
  locale,
  isVerifiedId,
  jobId,
  applicationCorrelationId,
  jobApplicationId,
  seekCandidateId,
  candidateId,
  onDelete,
}: Props) => {
  const { language } = useAppConfig();

  const onClick = () => {
    const downloadUrl = attachmentUrlV2({
      attachmentId: attachmentItem.attachmentId,
      attachmentType: 'CandidateSupportDoc',
      actionType: 'download',
      jobId,
      applicationCorrelationId,
      version: '0:0',
      language,
    });

    const documentType = 'hirer uploaded';

    downloadFile(downloadUrl, () => {
      trackDownloadDocumentEvent({
        applicationCorrelationId,
        applicationId: jobApplicationId,
        attachmentId: attachmentItem.attachmentId,
        seekCandidateId,
        adcentreCandidateId: candidateId,
        currentPage: TRACKING_CANDIDATE_DETAILS,
        documentType,
        isVerifiedId,
      });
    });
  };

  const MAX_SHORT_NAME_LENGTH = 45;

  const getShortFileName = (fileName: string) => {
    const nameLength = fileName.length;
    if (nameLength <= MAX_SHORT_NAME_LENGTH) {
      return fileName;
    }
    let splitIndex = fileName.lastIndexOf('.');
    if (splitIndex > 1) {
      splitIndex = splitIndex - 1;
    } else {
      splitIndex = nameLength - 5;
    }
    const splitString = '...';
    const firstPartIndexEnd =
      MAX_SHORT_NAME_LENGTH - (nameLength - splitIndex) - splitString.length;
    const temp = `${fileName.slice(
      0,
      firstPartIndexEnd,
    )}${splitString}${fileName.slice(splitIndex, nameLength)}`;
    return temp;
  };

  return (
    <>
      <Box data-testid="attachments" style={{ paddingBottom: '4px' }}>
        <Item
          type="attachment"
          user={attachmentItem.uploadedUser ?? ''}
          date={attachmentItem.uploadedDate ?? ''}
          canDelete={true}
          onDelete={() => onDelete(attachmentItem)}
          locale={locale}
        >
          <Text baseline={false} size="small" tone="neutral">
            <TextLinkButton onClick={onClick} weight="weak">
              {attachmentItem.fileName &&
                getShortFileName(attachmentItem.fileName)}
            </TextLinkButton>
          </Text>
        </Item>
      </Box>
      <Divider />
    </>
  );
};
